import {
  API_ROOT,
  CLIENT_HEADERS,
  devEnv,
  isMockprod,
  isStaging,
} from '#src/config.ts';

import { loadCookie } from './cookies.ts';
import { getAnalyticsHeaders } from './sessionHeaders.ts';
import { toCamelCase } from './toCamelCase.ts';

const xsrfHeaderName = 'X-CSRF-Token';
export const xsrfCookieName = devEnv ? `csrftoken_${devEnv}` : 'csrftoken';
export const xsrfReceivingHeaderName = 'csrf-token';

export const defaultHeaders: Record<string, string> = {
  ...(import.meta.env.VITE_CLOUDFLARE_ACCESS_CLIENT_ID &&
  (isStaging || isMockprod)
    ? {
        'CF-Access-Client-Id': import.meta.env
          .VITE_CLOUDFLARE_ACCESS_CLIENT_ID as string,
        'CF-Access-Client-Secret': import.meta.env
          .VITE_CLOUDFLARE_ACCESS_CLIENT_SECRET as string,
      }
    : {}),
  ...CLIENT_HEADERS,
};

export const deviceFingerprintRef = { current: '' };

function getXsrfHeader(): Record<string, string> {
  const token = loadCookie(xsrfCookieName);
  if (token && typeof token === 'string') {
    return { [xsrfHeaderName]: token };
  }
  return {};
}

export interface HttpResponse<T> {
  data: T;
  status: number;
  headers: Headers;
}

class HttpError extends Error {
  response: { data: unknown; status: number };

  constructor(message: string, response: { data: unknown; status: number }) {
    super(message);
    this.response = response;
  }
}

function isAbsoluteURL(url: string): boolean {
  return /^https?:\/\//i.test(url);
}

async function request<T>(
  url: string,
  options: RequestInit & { baseURL?: string } = {}
): Promise<HttpResponse<T>> {
  const { baseURL, ...fetchOptions } = options;
  const fullUrl = baseURL && !isAbsoluteURL(url) ? `${baseURL}${url}` : url;

  const response = await fetch(fullUrl, fetchOptions);

  if (!response.ok) {
    let data: unknown;
    try {
      data = await response.json();
    } catch {
      data = await response.text().catch(() => null);
    }
    throw new HttpError(`Request failed with status code ${response.status}`, {
      data,
      status: response.status,
    });
  }

  const contentType = response.headers.get('content-type');
  let data: T;
  if (contentType?.includes('application/json')) {
    data = await response.json();
  } else {
    data = (await response.text()) as unknown as T;
  }

  return { data, status: response.status, headers: response.headers };
}

interface HttpClient {
  get: <T>(url: string) => Promise<HttpResponse<T>>;
  post: <T>(url: string, body?: unknown) => Promise<HttpResponse<T>>;
  put: <T>(url: string, body?: unknown) => Promise<HttpResponse<T>>;
  delete: <T>(url: string) => Promise<HttpResponse<T>>;
}

function createClient({ withCredentials = true } = {}): HttpClient {
  const credentials: RequestCredentials = withCredentials ? 'include' : 'omit';

  function buildInit(
    method: string,
    body?: unknown
  ): RequestInit & { baseURL: string } {
    const headers: Record<string, string> = {
      ...defaultHeaders,
      ...(withCredentials && deviceFingerprintRef.current
        ? { DEVICE_FINGERPRINT: deviceFingerprintRef.current }
        : {}),
      ...(withCredentials ? getXsrfHeader() : {}),
      ...(withCredentials ? getAnalyticsHeaders() : {}),
    };

    const init: RequestInit & { baseURL: string } = {
      method,
      credentials,
      headers,
      baseURL: API_ROOT,
    };

    if (body !== undefined) {
      if (body instanceof FormData) {
        init.body = body;
      } else {
        headers['Content-Type'] = 'application/json';
        init.body = JSON.stringify(body);
      }
    }

    return init;
  }

  return {
    get: async <T>(url: string) => request<T>(url, buildInit('GET')),
    post: async <T>(url: string, body?: unknown) =>
      request<T>(url, buildInit('POST', body)),
    put: async <T>(url: string, body?: unknown) =>
      request<T>(url, buildInit('PUT', body)),
    delete: async <T>(url: string) => request<T>(url, buildInit('DELETE')),
  };
}

export const client = createClient({ withCredentials: true });
export const clientWithoutCredentials = createClient({
  withCredentials: false,
});

export const onError = (error: any) => {
  if (error.response) {
    return error.response.data;
  }
  return { error: error.message };
};

export const formatUpdateUserErrors = (errors: any) =>
  Object.entries<any>(errors).reduce<Record<string, string>>((sum, cur) => {
    const [key, [err]] = cur;
    sum[toCamelCase(key)] = err;
    return sum;
  }, {});

export async function load<T extends NonNullable<unknown>>(
  url: string,
  withCredentials = false
): Promise<T> {
  if (withCredentials) {
    const { data } = await client.get<T>(url);
    return data;
  }
  const response = await request(url);
  return response.data as T;
}
